import { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  CssBaseline, AppBar, Toolbar, Typography,
  Tabs, Tab, Container, IconButton, Tooltip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Divider, CircularProgress,
} from '@mui/material'
import { AUTH_REQUIRED_EVENT, checkAuth } from './api/client'
import { setToken } from './auth'
import { createTheme, ThemeProvider, alpha } from '@mui/material/styles'
import KeyIcon from '@mui/icons-material/Key'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import WebhookOutlinedIcon from '@mui/icons-material/WebhookOutlined'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import PipelinesPage from './features/pipelines/PipelinesPage'
import WebhooksPage from './features/webhooks/WebhooksPage'
import RunbooksPage from './features/runbooks/RunbooksPage'
import GuardLogPage from './features/workitems/GuardLogPage'
import BugSourceLogPage from './features/workitems/BugSourceLogPage'
import ScrumPage from './features/scrum/ScrumPage'
import CostPage from './features/cost/CostPage'
import ReleasesPage from './features/releases/ReleasesPage'
import VersionMapPage from './features/version-map/VersionMapPage'
import OrgCompliancePage from './features/compliance/OrgCompliancePage'
import EfMigrationPage from './features/efmigrations/EfMigrationPage'
import ApiInvocationsPage from './features/invocations/ApiInvocationsPage'

const BRAND = '#0f172a'   // slate-900
const ACCENT = '#3b82f6'  // blue-500

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: ACCENT },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
    h6: { fontWeight: 700, letterSpacing: '-0.01em' },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: BRAND, boxShadow: 'none', borderBottom: 'none' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: { borderColor: '#e2e8f0' },
        elevation1: { boxShadow: '0 1px 3px 0 rgb(0 0 0 / .08), 0 1px 2px -1px rgb(0 0 0 / .08)' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: alpha('#ffffff', 0.6),
          fontWeight: 500,
          fontSize: 13,
          letterSpacing: '0.02em',
          minHeight: 44,
          padding: '0 16px',
          textTransform: 'none',
          '&.Mui-selected': { color: '#ffffff' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: { '& .MuiTableCell-root': { fontWeight: 600, color: '#475569', fontSize: 12, letterSpacing: '0.05em', textTransform: 'uppercase' } },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
        containedPrimary: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
  },
})

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 5_000 } },
})

const TABS = [
  { label: 'Pipelines',  slug: 'pipelines',  icon: <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: 'Webhooks',   slug: 'webhooks',   icon: <WebhookOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: 'Runbooks',   slug: 'runbooks',   icon: <PlayCircleOutlineIcon sx={{ fontSize: 16 }} /> },
  { label: 'Guard Log',  slug: 'guard-log',  icon: <ShieldOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: 'Bug Source', slug: 'bug-source', icon: <BugReportOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: 'Scrum',      slug: 'scrum',      icon: <AutoStoriesOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: 'Cost',       slug: 'cost',       icon: <AssessmentOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: 'Releases',      slug: 'releases',      icon: <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: 'Environments',  slug: 'environments',  icon: <LayersOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: 'Compliance',    slug: 'compliance',    icon: <FactCheckOutlinedIcon  sx={{ fontSize: 16 }} /> },
  { label: 'EF Migrations', slug: 'ef-migrations', icon: <StorageOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: 'Invocations',   slug: 'invocations',   icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 16 }} /> },
]

function getInitialTab(): number {
  const hash = window.location.hash.replace('#', '')
  const idx = TABS.findIndex(t => t.slug === hash)
  return idx >= 0 ? idx : 0
}

type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated'

export default function App() {
  const [tab, setTab] = useState(getInitialTab)
  const [keyOpen, setKeyOpen] = useState(false)
  const [keyInput, setKeyInput] = useState(localStorage.getItem('devops-api-key') ?? '')
  const [authStatus, setAuthStatus] = useState<AuthStatus>('checking')

  useEffect(() => {
    checkAuth().then((ok) => setAuthStatus(ok ? 'authenticated' : 'unauthenticated'))
  }, [])

  useEffect(() => {
    // api/client.ts dispatches this if any later /api/* call comes back
    // unauthenticated (e.g. the session expired mid-use) — drop back to the
    // sign-in gate rather than leaving a half-broken dashboard on screen.
    const onAuthRequired = () => setAuthStatus('unauthenticated')
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired)
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired)
  }, [])

  function saveKey() {
    localStorage.setItem('devops-api-key', keyInput)
    setKeyOpen(false)
  }

  function signIn() {
    // A small popup for the real oauth2-proxy/Entra login flow, so the host
    // page (Pulse) never navigates away. A relative path, not an absolute
    // URL — oauth2-proxy only preserves the path portion of rd (confirmed:
    // it silently drops any query string), and only accepts absolute targets
    // for whitelisted domains, so a plain relative path is what survives.
    const target = `${window.location.origin}/oauth2/start?rd=${encodeURIComponent('/login-complete')}`
    const popup = window.open(target, 'devops-api-login', 'width=480,height=640')

    if (!popup) {
      // Popup blocked — fall back to a full top-level navigation, carrying
      // the return URL as a base64url path segment (again: query strings on
      // rd don't survive oauth2-proxy, only the path does). window.top
      // .location can be SET (though not read) even across origins/frames,
      // so this reliably breaks out of Pulse's iframe when embedded.
      const returnTo = (window.top !== window.self && document.referrer)
        || (window.location.pathname + window.location.search)
      const encoded = btoa(encodeURIComponent(returnTo)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
      ;(window.top ?? window).location.href =
        `${window.location.origin}/oauth2/start?rd=${encodeURIComponent(`/login-complete/${encoded}`)}`
      return
    }

    // The popup's own /login-complete page fetches a token there (its cookie
    // works fine — it's a genuine top-level context) and relays it back here
    // via postMessage, since the cookie itself can't be read from this,
    // possibly-embedded, window. Ping repeatedly since we don't know exactly
    // when that page's listener becomes ready.
    const openedPopup = popup
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== 'devops-api:token') return
      window.removeEventListener('message', onMessage)
      clearInterval(pinger)
      if (event.data.token) {
        setToken(event.data.token)
        setAuthStatus('authenticated')
      }
      try { openedPopup.close() } catch { /* already closed */ }
    }
    window.addEventListener('message', onMessage)

    const pinger = setInterval(() => {
      let closed = true
      try { closed = openedPopup.closed } catch { /* isolated — assume closed */ }
      if (closed) {
        clearInterval(pinger)
        window.removeEventListener('message', onMessage)
        return
      }
      try { openedPopup.postMessage({ type: 'devops-api:request-token' }, window.location.origin) } catch { /* not ready yet */ }
    }, 400)
  }

  if (authStatus !== 'authenticated') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 2.5, bgcolor: BRAND, px: 3,
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '9px', bgcolor: ACCENT,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 18, lineHeight: 1 }}>S</Typography>
          </Box>
          {authStatus === 'checking' ? (
            <CircularProgress size={22} sx={{ color: alpha('#ffffff', 0.6) }} />
          ) : (
            <>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>
                Sign in to Skillable DevOps
              </Typography>
              <Button variant="contained" onClick={signIn}>Sign in</Button>
            </>
          )}
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>

        <AppBar position="sticky" elevation={0}>
          <Toolbar variant="dense" sx={{ minHeight: 52, px: { xs: 2, sm: 3 } }}>
            {/* Logo mark */}
            <Box sx={{
              width: 28, height: 28, borderRadius: '6px', bgcolor: ACCENT,
              display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0,
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 14, lineHeight: 1 }}>S</Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff', letterSpacing: '-0.01em', flexGrow: 1 }}>
              Skillable DevOps
            </Typography>
            <Tooltip title="Set API Key">
              <IconButton size="small" onClick={() => setKeyOpen(true)}
                sx={{ color: alpha('#fff', 0.7), '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.08) } }}>
                <KeyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Toolbar>

          <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />

          <Tabs
            value={tab}
            onChange={(_: unknown, v: number) => { setTab(v); window.location.hash = TABS[v].slug }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ px: { xs: 1, sm: 2 }, minHeight: 44 }}
            TabIndicatorProps={{ style: { height: 2, backgroundColor: ACCENT, borderRadius: '2px 2px 0 0' } }}
          >
            {TABS.map(t => (
              <Tab key={t.label} label={t.label} icon={t.icon} iconPosition="start"
                disableRipple sx={{ gap: 0.75 }} />
            ))}
          </Tabs>
        </AppBar>

        <Container maxWidth="xl" sx={{ py: 3 }}>
          {tab === 0 && <PipelinesPage />}
          {tab === 1 && <WebhooksPage />}
          {tab === 2 && <RunbooksPage />}
          {tab === 3 && <GuardLogPage />}
          {tab === 4 && <BugSourceLogPage />}
          {tab === 5 && <ScrumPage />}
          {tab === 6 && <CostPage />}
          {tab === 7 && <ReleasesPage />}
          {tab === 8 && <VersionMapPage />}
          {tab === 9 && <OrgCompliancePage />}
          {tab === 10 && <EfMigrationPage />}
          {tab === 11 && <ApiInvocationsPage />}
        </Container>

        <Dialog open={keyOpen} onClose={() => setKeyOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>API Key</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Required for triggering pipelines and runbooks. Stored in localStorage.
            </Typography>
            <TextField
              label="X-Api-Key" fullWidth size="small" type="password"
              value={keyInput} onChange={e => setKeyInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveKey()}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setKeyOpen(false)} color="inherit">Cancel</Button>
            <Button variant="contained" onClick={saveKey}>Save</Button>
          </DialogActions>
        </Dialog>

      </QueryClientProvider>
    </ThemeProvider>
  )
}
